/**
 * Graph model of the company network view (design: design/v2/netzwerk.html):
 * turns the backend network (GET /companies/{id}/network) into rows around the
 * focal company — owners above it, management, Prokura and holdings below —
 * with one edge per connected pair. Pure; the pixel layout is in layout.ts.
 *
 * Row rules, applied outward from the focal (row 0) in BFS order:
 *   - an owner sits one row above what it owns (relaxed afterwards, so an
 *     owner of an owner climbs another row);
 *   - a general partner (Komplementär / persönlich haftende(r)
 *     Gesellschafter(in)) counts as an owner for placement: it sits above the
 *     partnership, so the GmbH of a GmbH & Co. KG sits between the KG and the
 *     family owning both — instead of below the KG with every family line
 *     cutting through the KG's row;
 *   - other role-bearers (Geschäftsführer, Prokurist, …) and a company reached
 *     through one sit one row further out — below the focal, above an owner;
 *   - capital edges ending at or above row 0 are shareholdings ("own"), those
 *     ending below it are holdings ("sub").
 * Rows are computed on the full graph, so the role filters hide nodes without
 * moving the rest around.
 */
import type { CompanyNetworkResponse, NetworkNode } from '@/lib/backend-api';
import {
  isManagingRole,
  isSubsidiaryRole,
  isUpstreamRole,
  mergeSameNameNodes,
  normaliseEdges,
} from '@/lib/network-graph';

export type EdgeKind = 'own' | 'manage' | 'proc' | 'sub';
export const EDGE_KINDS: EdgeKind[] = ['own', 'manage', 'proc', 'sub'];

export interface GraphNode {
  id: string;
  type: 'center' | 'company' | 'person' | 'group';
  name: string;
  raw: NetworkNode | null;
  row: number;
  /** Hops from the focal over the visible edges. */
  dist: number;
  /** Group nodes: the folded leaves, sorted by name. */
  members?: GraphNode[];
}

export interface GraphEdge {
  id: string;
  /** Upper end (owner, or the node nearer the focal on the way out). */
  up: string;
  down: string;
  kinds: EdgeKind[];
  /** Ownership share in %, when known. */
  share: number | null;
  /** Raw (German) backend roles, deduplicated. */
  roles: string[];
  capital: boolean;
  /** Who is placed above whom: capital owner, or a general partner. */
  placeOwner: string | null;
}

export interface NetworkGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
  /** Kinds present in the full graph (filter chips are only shown for these). */
  kinds: EdgeKind[];
  /** Deepest hop in the full graph. */
  maxDist: number;
}

interface PairEdge {
  a: string;
  b: string;
  /** Capital edges only: owner → owned. */
  owner: string | null;
  /** Role edges only: the role-bearer. */
  bearer: string | null;
  /** The role is general partner: placed like an owner (see assignRows). */
  partner: boolean;
  capital: boolean;
  roleKinds: Set<'manage' | 'proc'>;
  share: number | null;
  roles: string[];
}

const ROW_LIMIT = 4;
/** Leaves on one node with the same role fold into a list box from this many. */
export const GROUP_MIN = 5;

function edgeClass(role: string): 'capital' | 'manage' | 'proc' {
  if (/prokur/i.test(role)) return 'proc';
  if (isManagingRole(role)) return 'manage';
  if (isSubsidiaryRole(role) || isUpstreamRole(role) || /hauptniederlassung/i.test(role))
    return 'capital';
  // Rechtsnachfolger, Vertreter, … — a role, not capital.
  return 'manage';
}

/**
 * A Kommanditist's % is only meaningful in a GmbH/UG/AG & Co. KG: there the
 * Komplementär is a company without a stake, so the Kommanditanteile add up
 * to the whole. In a plain KG the Komplementär's (natural person's) stake is
 * not in the register, so a Kommanditist's % of the published sum says
 * nothing about real ownership — same rule as the people list
 * (PeopleTabbedCard showKommanditPercent). After normaliseEdges the target is
 * the KG.
 */
function shareShown(role: string, ownedName: string): boolean {
  return !/kommanditist/i.test(role) || /[&+]\s*Co\./i.test(ownedName);
}

function pairKey(a: string, b: string) {
  return a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`;
}

/** One edge per connected pair, with roles, share and orientation merged. */
function mergePairs(data: CompanyNetworkResponse): {
  nodeById: Map<string, NetworkNode>;
  pairs: PairEdge[];
} {
  const merged = mergeSameNameNodes(data);
  const nodeById = new Map(merged.nodes.map((n) => [n.id, n]));
  const edges = normaliseEdges(merged.edges, nodeById);
  const byKey = new Map<string, PairEdge>();
  for (const e of edges) {
    if (e.source === e.target || !nodeById.has(e.source) || !nodeById.has(e.target)) continue;
    const key = pairKey(e.source, e.target);
    let p = byKey.get(key);
    if (!p) {
      p = {
        a: e.source,
        b: e.target,
        owner: null,
        bearer: null,
        partner: false,
        capital: false,
        roleKinds: new Set(),
        share: null,
        roles: [],
      };
      byKey.set(key, p);
    }
    const cls = edgeClass(e.role ?? '');
    // After normaliseEdges the source is the owner / parent / role-bearer.
    if (cls === 'capital') {
      if (!p.capital) {
        p.capital = true;
        p.owner = e.source;
      }
    } else {
      p.roleKinds.add(cls);
      p.bearer ??= e.source;
      if (/persönlich haftend|persoenlich haftend|komplement/i.test(e.role ?? '')) p.partner = true;
    }
    const share = shareShown(e.role ?? '', nodeById.get(e.target)?.label ?? '') ? e.share_percent : null;
    if (share != null && (p.share == null || share > p.share)) p.share = share;
    const role = (e.role ?? '').trim();
    if (role && !p.roles.includes(role)) p.roles.push(role);
  }
  return { nodeById, pairs: [...byKey.values()] };
}

/** Who sits above whom: the owner of a capital edge, else a general partner. */
function placementOwner(p: PairEdge): string | null {
  if (p.capital) return p.owner;
  return p.partner ? p.bearer : null;
}

interface RowEdge {
  a: string;
  b: string;
  /** Placed above the other end, or null for a plain role edge. */
  owner: string | null;
}

function assignRows(focal: string, pairs: RowEdge[]): Map<string, number> {
  const adj = new Map<string, RowEdge[]>();
  for (const p of pairs) {
    for (const id of [p.a, p.b]) {
      if (!adj.has(id)) adj.set(id, []);
      adj.get(id)!.push(p);
    }
  }
  const row = new Map<string, number>([[focal, 0]]);
  const queue = [focal];
  const out = (r: number) => r + (r < 0 ? -1 : 1);
  while (queue.length) {
    const u = queue.shift()!;
    const ru = row.get(u)!;
    for (const p of adj.get(u) ?? []) {
      const v = p.a === u ? p.b : p.a;
      if (row.has(v)) continue;
      let rv: number;
      const owner = p.owner;
      if (owner) rv = owner === v ? ru - 1 : ru + 1;
      else rv = u === focal ? 1 : out(ru);
      row.set(v, Math.max(-ROW_LIMIT, Math.min(ROW_LIMIT, rv)));
      queue.push(v);
    }
  }
  // Owners strictly above what they own: an owner of an owner climbs, a
  // holding of a holding sinks. Bounded, so ownership cycles can't loop.
  for (let pass = 0; pass < 8; pass++) {
    let changed = false;
    for (const p of pairs) {
      const owner = p.owner;
      if (!owner) continue;
      const owned = owner === p.a ? p.b : p.a;
      const ro = row.get(owner);
      const rw = row.get(owned);
      if (ro === undefined || rw === undefined || ro < rw) continue;
      if (owned === focal || rw <= 0) {
        if (owner !== focal && rw - 1 >= -ROW_LIMIT) {
          row.set(owner, rw - 1);
          changed = true;
        }
      } else if (owned !== focal && ro + 1 <= ROW_LIMIT) {
        row.set(owned, ro + 1);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return row;
}

/** The full graph: every node and pair edge with its row, before filtering. */
export function buildFullGraph(data: CompanyNetworkResponse): {
  graph: NetworkGraph;
  nodeById: Map<string, NetworkNode>;
} {
  const focal = data.eu_id;
  const { nodeById, pairs } = mergePairs(data);
  const rows = assignRows(
    focal,
    pairs.map((p) => ({ a: p.a, b: p.b, owner: placementOwner(p) })),
  );
  const edges: GraphEdge[] = [];
  for (const p of pairs) {
    const ra = rows.get(p.a);
    const rb = rows.get(p.b);
    if (ra === undefined || rb === undefined) continue; // not connected to the focal
    const kinds: EdgeKind[] = [];
    let up = ra <= rb ? p.a : p.b;
    if (p.capital && p.owner) {
      const owned = p.owner === p.a ? p.b : p.a;
      kinds.push((rows.get(owned) ?? 0) <= 0 ? 'own' : 'sub');
    }
    const owner = placementOwner(p);
    if (owner) {
      const owned = owner === p.a ? p.b : p.a;
      if (rows.get(owner)! < rows.get(owned)!) up = owner;
    }
    if (p.roleKinds.has('manage')) kinds.push('manage');
    if (p.roleKinds.has('proc')) kinds.push('proc');
    edges.push({
      id: pairKey(p.a, p.b),
      up,
      down: up === p.a ? p.b : p.a,
      kinds,
      share: p.share,
      roles: p.roles,
      capital: p.capital,
      placeOwner: owner,
    });
  }
  const nodes: GraphNode[] = [];
  for (const [id, row] of rows) {
    const raw = nodeById.get(id) ?? null;
    nodes.push({
      id,
      type: id === focal ? 'center' : raw?.node_type === 'person' ? 'person' : 'company',
      name: raw?.label ?? id,
      raw,
      row,
      dist: 0,
    });
  }
  const present = new Set(edges.flatMap((e) => e.kinds));
  return {
    graph: {
      nodes,
      edges,
      kinds: EDGE_KINDS.filter((k) => present.has(k)),
      maxDist: Math.max(0, ...bfs(focal, edges).values()),
    },
    nodeById,
  };
}

function bfs(focal: string, edges: GraphEdge[]): Map<string, number> {
  const adj = new Map<string, string[]>();
  for (const e of edges) {
    if (!adj.has(e.up)) adj.set(e.up, []);
    if (!adj.has(e.down)) adj.set(e.down, []);
    adj.get(e.up)!.push(e.down);
    adj.get(e.down)!.push(e.up);
  }
  const dist = new Map<string, number>([[focal, 0]]);
  const q = [focal];
  while (q.length) {
    const u = q.shift()!;
    for (const v of adj.get(u) ?? []) {
      if (dist.has(v)) continue;
      dist.set(v, dist.get(u)! + 1);
      q.push(v);
    }
  }
  return dist;
}

/** A Prokura-only edge that doesn't touch the focal company. */
export function isFarProcura(e: GraphEdge, focal: string): boolean {
  return e.kinds.length === 1 && e.kinds[0] === 'proc' && e.up !== focal && e.down !== focal;
}

const sameRoles = (a: string[], b: string[]) =>
  a.length === b.length && a.every((r) => b.includes(r));

/**
 * The visible graph: edges of the enabled kinds, nodes still connected to the
 * focal through them and within `depth` hops (null = all), with large sets of
 * same-role leaves on one node folded into a group (unless expanded). Prokura
 * between other nodes is left out unless `farProcura` — people holding
 * Prokura in a dozen sister companies otherwise weave a dense band of lines.
 */
export function visibleGraph(
  full: NetworkGraph,
  focal: string,
  opts: {
    kinds: Set<EdgeKind>;
    depth: number | null;
    expanded: Set<string>;
    /** Prokura of other companies than the focal (dense, rarely the question). */
    farProcura: boolean;
  },
): { nodes: GraphNode[]; edges: GraphEdge[] } {
  let edges = full.edges.filter(
    (e) =>
      e.kinds.some((k) => opts.kinds.has(k)) &&
      (opts.farProcura || !isFarProcura(e, focal)),
  );
  const dist = bfs(focal, edges);
  const keep = (id: string) => {
    const d = dist.get(id);
    return d !== undefined && (opts.depth == null || d <= opts.depth);
  };
  // Level d: the links of everything within d − 1 hops — level 1 is the
  // company's own links only, not those between its neighbours.
  edges = edges.filter(
    (e) =>
      keep(e.up) &&
      keep(e.down) &&
      (opts.depth == null || Math.min(dist.get(e.up)!, dist.get(e.down)!) < opts.depth),
  );
  // Rows for what is shown, so hidden links don't leave holes or detours.
  const rows = assignRows(focal, edges.map((e) => ({ a: e.up, b: e.down, owner: e.placeOwner })));
  // copies: the full graph's edges stay as they are
  edges = edges.map((e) => {
    const ru = rows.get(e.up);
    const rd = rows.get(e.down);
    return ru !== undefined && rd !== undefined && ru > rd ? { ...e, up: e.down, down: e.up } : { ...e };
  });
  const nodes = full.nodes
    .filter((n) => keep(n.id) && rows.has(n.id))
    .map((n) => ({ ...n, row: rows.get(n.id)!, dist: dist.get(n.id) ?? 0 }));

  const degree = new Map<string, number>();
  for (const e of edges) {
    degree.set(e.up, (degree.get(e.up) ?? 0) + 1);
    degree.set(e.down, (degree.get(e.down) ?? 0) + 1);
  }
  const byId = new Map(nodes.map((n) => [n.id, n]));
  // hub → candidate leaf buckets keyed by (row, node type, roles)
  const buckets = new Map<string, { hub: string; edge: GraphEdge; leaves: GraphNode[] }>();
  for (const e of edges) {
    if (e.share != null) continue;
    for (const [hub, leaf] of [
      [e.up, e.down],
      [e.down, e.up],
    ] as const) {
      const ln = byId.get(leaf);
      if (!ln || ln.type === 'center' || degree.get(leaf) !== 1) continue;
      const key = `group:${hub}:${ln.row}:${ln.type}:${[...e.roles].sort().join('|')}:${e.kinds.join('|')}`;
      const b = buckets.get(key);
      if (b && sameRoles(b.edge.roles, e.roles)) b.leaves.push(ln);
      else if (!b) buckets.set(key, { hub, edge: e, leaves: [ln] });
    }
  }
  const folded = new Set<string>();
  const groupNodes: GraphNode[] = [];
  const groupEdges: GraphEdge[] = [];
  for (const [key, b] of buckets) {
    if (b.leaves.length < GROUP_MIN || opts.expanded.has(key)) continue;
    const members = [...b.leaves].sort((x, y) => x.name.localeCompare(y.name, 'de'));
    for (const m of members) folded.add(m.id);
    const leaf = members[0];
    groupNodes.push({
      id: key,
      type: 'group',
      name: '',
      raw: null,
      row: leaf.row,
      dist: leaf.dist,
      members,
    });
    const hubIsUp = b.edge.up === b.hub;
    groupEdges.push({
      ...b.edge,
      id: `${key}:edge`,
      up: hubIsUp ? b.hub : key,
      down: hubIsUp ? key : b.hub,
    });
  }
  return {
    nodes: [...nodes.filter((n) => !folded.has(n.id)), ...groupNodes],
    edges: [
      ...edges.filter((e) => !folded.has(e.up) && !folded.has(e.down)),
      ...groupEdges,
    ],
  };
}
